import {fromBER} from 'asn1js';
import * as uuid from 'uuid';

export enum ContentType {
  PNG,
  JPEG,
  BMP,
  GIF,
  PDF,
  UNKNOWN,
  ZIP,
  MSG,
  EML,
  RTF,
  XLSX
}

export default class FileUtils {
  /**
   * check if the file is X509 or not. it returns a boolean result.
   *
   * @param blob : the file
   */
  public static async checkIfX509File(blob: any): Promise<boolean> {
    let r: boolean | PromiseLike<boolean>;
    await FileUtils.readFileContent(blob, true).then(rs => {
      const certificateBuffer = FileUtils.readPemContent(rs);
      const asn1 = fromBER(certificateBuffer);
      r = asn1.offset !== -1;
    });

    return new Promise((resolve, reject) => {
      resolve(r);
    });
  }

  private static readPemContent(pem: string) {
    const lines = pem.split('\n');
    let encoded = '';
    for (const line of lines) {
      if (
        line.trim().length > 0 &&
        line.indexOf('-BEGIN RSA PRIVATE KEY-') < 0 &&
        line.indexOf('-BEGIN RSA PUBLIC KEY-') < 0 &&
        line.indexOf('-BEGIN PUBLIC KEY-') < 0 &&
        line.indexOf('-BEGIN CERTIFICATE-') < 0 &&
        line.indexOf('-BEGIN PRIVATE KEY-') < 0 &&
        line.indexOf('-END PRIVATE KEY-') < 0 &&
        line.indexOf('-END CERTIFICATE-') < 0 &&
        line.indexOf('-END PUBLIC KEY-') < 0 &&
        line.indexOf('-END RSA PRIVATE KEY-') < 0 &&
        line.indexOf('-END RSA PUBLIC KEY-') < 0
      ) {
        encoded += line.trim();
      }
    }
    return FileUtils.base64StringToArrayBuffer(encoded);
  }

  private static base64StringToArrayBuffer(b64str) {
    const byteStr = atob(b64str);

    const bytes = new Uint8Array(byteStr.length);
    for (let i = 0; i < byteStr.length; i++) {
      bytes[i] = byteStr.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private static readFileContent(blob: any, readAsText: boolean): Promise<any> {
    const temporaryFileReader = new FileReader();

    return new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort();
        reject(new DOMException('Problem parsing input file.'));
      };

      temporaryFileReader.onloadend = () => {
        resolve(temporaryFileReader.result);
      };
      if (readAsText) {
        temporaryFileReader.readAsText(blob);
      } else {
        temporaryFileReader.readAsArrayBuffer(blob);
      }
    });
  }

  /**
   * return a content-type in a promise. available return values are:
   * + image/png
   * + image/gif
   * + image/jpeg
   * + application/pdf
   * + application/zip
   * + Unknown filetype
   *
   * @param blob the file
   */
  public static async getContentType(blob: any): Promise<ContentType> {
    let r: ContentType | PromiseLike<ContentType>;
    await FileUtils.readFileContent(blob, false).then(rs => {
      const arr = new Uint8Array(rs).subarray(0, 4);
      let header = '';
      for (const ar of arr) {
        header += ar.toString(16);
      }
      r = FileUtils.determineContentTypeFromHeader(header.toUpperCase());
    });

    return new Promise((resolve, reject) => {
      resolve(r);
    });
  }

  private static determineContentTypeFromHeader(signature: string): ContentType {
    switch (true) {
      case /89504E47/.test(signature):
        return ContentType.PNG;
      case /47494638/.test(signature):
        return ContentType.GIF;
      case /25504446/.test(signature):
        return ContentType.PDF;
      case /424D.{3}/.test(signature):
        return ContentType.BMP;
      case /FFD8FF.{2}/.test(signature):
        return ContentType.JPEG;
      case /504B0304/.test(signature):
        return ContentType.ZIP;
      case /D0CF11E0/.test(signature): 
        return ContentType.MSG;
      case /7B5C7274/.test(signature): 
        return ContentType.RTF;
      case /504B/.test(signature):
        return ContentType.XLSX;
      default:
        return ContentType.UNKNOWN;
    }
  }

  public static downLoadFile(data: any, type: string, fileName: string) {
    const blob = new Blob([data], { type });
    if (window.top.navigator.msSaveOrOpenBlob) {
      // Store Blob in IE
      window.top.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      // Store Blob in others
      const tA = document.body.appendChild(document.createElement('a'));
      tA.href = URL.createObjectURL(blob);
      tA.download = fileName;
      tA.style.display = 'none';
      tA.click();
      tA.parentNode.removeChild(tA);
    }
  }

  public static getCodeOfFileWhenSignOnce(files: Array<any>){
    const result = new Map<any, any>();
    for (let i = 0; i < files.length; i++) {
      const fileId = this.decodeFileId(files[i].id);
      if (!result.has(fileId)) {
        result.set(fileId, uuid.v4());
      }
    }
    return result;
  }
  public static getCodeOfFileWhenNoDecodeSignOnce(files: Array<any>){
    const result = new Map<any, any>();
    for (let i = 0; i < files.length; i++) {
      const fileId = files[i].id;
      if (!result.has(fileId)) {
        result.set(fileId, uuid.v4());
      }
    }
    return result;
  }

  private static decodeFileId(id: string) {
    const idDecode = atob(id);
    const findNumber = idDecode.match(/[0-9]{1,}/g);
    if (findNumber && findNumber.length) {
      return Number(findNumber[findNumber.length - 1]);
    }
    return null;
  }


}
